import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './CustomerSignup.css';
import { createCustomerPublic, getNextCustomerId } from '../../services/customersService';

const CustomerSignup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customerName: '',
    panNumber: '',
    aadharNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    pincode: '',
    state: '',
    country: 'India',
    emailId: '',
    password: '',
    confirmPassword: '',
    mobileNumber: '',
    alternateContactNumber: '',
    photo: null,
    photoPreview: null,
    terms: false
  });
  const [errors, setErrors] = useState({});
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [generatedOTP, setGeneratedOTP] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Clear city when state changes
    if (name === 'state') {
      setFormData(prev => ({
        ...prev,
        state: value,
        city: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Customer Name validation
    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Customer name is required';
    } else if (formData.customerName.trim().length < 3) {
      newErrors.customerName = 'Customer name must be at least 3 characters';
    }

    // Mobile Number validation
    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^[0-9]{10}$/.test(formData.mobileNumber.replace(/[\s-]/g, ''))) {
      newErrors.mobileNumber = 'Mobile number must be 10 digits';
    }

    // Email validation
    if (formData.emailId && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId)) {
      newErrors.emailId = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    // Confirm Password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms validation
    if (!formData.terms) {
      newErrors.terms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Generate dummy OTP
  const generateOTP = () => {
    // Generate a 6-digit OTP (for demo, we'll use a fixed one: 123456)
    const dummyOTP = '123456';
    setGeneratedOTP(dummyOTP);
    return dummyOTP;
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({...formData, photo: file, photoPreview: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Create account directly without OTP verification
    try {
      setIsSubmitting(true);
      
      // Don't try to get customer ID - let backend auto-generate it
      // This avoids 401 errors from authenticated endpoints
      
      // Map form data to API format (same as customer management)
      // Only include fields that have values (don't send undefined or empty strings)
      const customerPayload = {
        customer_name: formData.customerName,
        password: formData.password,
        mobile_number: formData.mobileNumber,
        country: formData.country || 'India'
      };
      
      // Add optional fields only if they have values
      if (formData.panNumber && formData.panNumber.trim()) {
        customerPayload.pan_number = formData.panNumber.trim();
      }
      if (formData.aadharNumber && formData.aadharNumber.trim()) {
        customerPayload.aadhar_number = formData.aadharNumber.trim();
      }
      if (formData.addressLine1 && formData.addressLine1.trim()) {
        customerPayload.address_line1 = formData.addressLine1.trim();
      }
      if (formData.addressLine2 && formData.addressLine2.trim()) {
        customerPayload.address_line2 = formData.addressLine2.trim();
      }
      if (formData.city && formData.city.trim()) {
        customerPayload.city = formData.city.trim();
      }
      if (formData.pincode && formData.pincode.trim()) {
        customerPayload.pincode = formData.pincode.trim();
      }
      if (formData.state && formData.state.trim()) {
        customerPayload.state = formData.state.trim();
      }
      if (formData.emailId && formData.emailId.trim()) {
        customerPayload.email_id = formData.emailId.trim();
      }
      if (formData.alternateContactNumber && formData.alternateContactNumber.trim()) {
        customerPayload.alternate_contact_number = formData.alternateContactNumber.trim();
      }
      if (formData.photo) {
        customerPayload.photo = formData.photo;
      }

      // Create customer using public signup endpoint (no authentication required)
      await createCustomerPublic(customerPayload);

      // Show success
      setShowSuccessPopup(true);
    } catch (error) {
      console.error('Error creating customer:', error);
      
      // Extract error message from response
      let errorMessage = 'Failed to create account. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Handle specific error cases
      if (error.response?.status === 409) {
        // Conflict - duplicate email or mobile
        errorMessage = errorMessage || 'Email or mobile number already exists. Please use different credentials.';
      } else if (error.response?.status === 400) {
        // Bad request - validation error
        errorMessage = errorMessage || 'Please check your input and try again.';
      }
      
      setErrors(prev => ({ ...prev, general: errorMessage }));
      // Don't use alert - show error in form instead
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError('');

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      setOtpError('');
      // Focus last input
      document.getElementById('otp-5').focus();
    }
  };

  const handleVerifyOTP = async () => {
    const enteredOTP = otp.join('');
    
    if (enteredOTP.length !== 6) {
      setOtpError('Please enter complete 6-digit OTP');
      return;
    }

    if (enteredOTP === generatedOTP) {
      // OTP verified successfully, now create the customer
      try {
        setIsSubmitting(true);
        
        // Don't try to get customer ID - let backend auto-generate it
        // Map form data to API format (same as customer management)
        const customerPayload = {
          // Don't include customer_code - let backend auto-generate
          customer_name: formData.customerName,
          pan_number: formData.panNumber || undefined,
          aadhar_number: formData.aadharNumber || undefined,
          address_line1: formData.addressLine1 || undefined,
          address_line2: formData.addressLine2 || undefined,
          city: formData.city || undefined,
          pincode: formData.pincode || undefined,
          state: formData.state || undefined,
          country: formData.country || 'India',
          email_id: formData.emailId || undefined,
          password: formData.password,
          mobile_number: formData.mobileNumber,
          alternate_contact_number: formData.alternateContactNumber || undefined,
          photo: formData.photo || undefined
        };

        // Create customer using public signup (no authentication required)
        await createCustomerPublic(customerPayload);

        // Show success
        setShowOTPVerification(false);
        setShowSuccessPopup(true);
        setOtp(['', '', '', '', '', '']);
        setOtpError('');
      } catch (error) {
        console.error('Error creating customer:', error);
        const errorMessage = error.message || 'Failed to create account. Please try again.';
        setOtpError(errorMessage);
        alert(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setOtpError('Invalid OTP. Please try again.');
    }
  };

  const handleResendOTP = () => {
    const newOTP = generateOTP();
    setOtp(['', '', '', '', '', '']);
    setOtpError('');
    // In real app, this would resend OTP to email
    console.log('OTP resent:', newOTP);
  };

  const handleClosePopup = () => {
    setShowSuccessPopup(false);
    // Navigate to login after closing popup
    setTimeout(() => {
      navigate('/login');
    }, 500);
  };

  // Indian states and cities mapping (same as customer management)
  const indianStatesAndCities = {
    'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Kurnool', 'Rajahmundry', 'Tirupati', 'Kakinada', 'Kadapa', 'Anantapur', 'Eluru', 'Ongole', 'Nandyal', 'Machilipatnam', 'Adoni', 'Tenali', 'Chittoor', 'Hindupur', 'Proddatur', 'Bhimavaram'],
    'Arunachal Pradesh': ['Itanagar', 'Naharlagun', 'Tawang', 'Bomdila', 'Pasighat', 'Tezu', 'Ziro', 'Along', 'Daporijo', 'Namsai', 'Roing', 'Khonsa', 'Aalo', 'Seppa', 'Yingkiong'],
    'Assam': ['Guwahati', 'Silchar', 'Dibrugarh', 'Jorhat', 'Nagaon', 'Tinsukia', 'Tezpur', 'Bongaigaon', 'Dhubri', 'Diphu', 'North Lakhimpur', 'Karimganj', 'Goalpara', 'Barpeta', 'Sivasagar', 'Hailakandi', 'Golaghat', 'Dhekiajuli', 'Mangaldoi'],
    'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Purnia', 'Darbhanga', 'Arrah', 'Bihar Sharif', 'Katihar', 'Munger', 'Chapra', 'Sasaram', 'Hajipur', 'Sitamarhi', 'Samastipur', 'Bettiah', 'Motihari', 'Saharsa', 'Buxar', 'Kishanganj'],
    'Chhattisgarh': ['Raipur', 'Bhilai', 'Bilaspur', 'Durg', 'Korba', 'Rajpur', 'Raigarh', 'Jagdalpur', 'Ambikapur', 'Chirmiri', 'Dhamtari', 'Dantewada', 'Kanker', 'Kawardha', 'Mahasamund', 'Surajpur'],
    'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda', 'Bicholim', 'Valpoi', 'Curchorem', 'Quepem', 'Canacona'],
    'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Gandhinagar', 'Junagadh', 'Gandhidham', 'Anand', 'Navsari', 'Morbi', 'Nadiad', 'Surendranagar', 'Bharuch', 'Mehsana', 'Bhuj', 'Porbandar', 'Palanpur', 'Valsad'],
    'Haryana': ['Faridabad', 'Gurgaon', 'Panipat', 'Ambala', 'Yamunanagar', 'Rohtak', 'Hisar', 'Karnal', 'Sonipat', 'Panchkula', 'Sirsa', 'Bhiwani', 'Bahadurgarh', 'Jind', 'Thanesar', 'Kaithal', 'Rewari', 'Palwal', 'Fatehabad', 'Hansi'],
    'Himachal Pradesh': ['Shimla', 'Mandi', 'Solan', 'Dharamshala', 'Bilaspur', 'Kullu', 'Chamba', 'Hamirpur', 'Una', 'Kangra', 'Nahan', 'Palampur', 'Sundarnagar', 'Nurpur', 'Keylong'],
    'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro Steel City', 'Hazaribagh', 'Deoghar', 'Giridih', 'Adityapur', 'Ramgarh', 'Medininagar', 'Chirkunda', 'Phusro', 'Chaibasa', 'Gumla', 'Lohardaga', 'Dumka', 'Pakur', 'Godda'],
    'Karnataka': ['Bangalore', 'Mysore', 'Hubli', 'Mangalore', 'Belagavi', 'Gulbarga', 'Davangere', 'Bellary', 'Bijapur', 'Shimoga', 'Tumkur', 'Raichur', 'Bidar', 'Hospet', 'Hassan', 'Udupi', 'Gadag', 'Chitradurga', 'Kolar', 'Mandya'],
    'Kerala': ['Kochi', 'Kozhikode', 'Thiruvananthapuram', 'Thrissur', 'Kollam', 'Kannur', 'Alappuzha', 'Kottayam', 'Palakkad', 'Manjeri', 'Thalassery', 'Ponnani', 'Kochi', 'Kayamkulam', 'Kottarakkara', 'Changanassery', 'Kasaragod', 'Pathanamthitta', 'Idukki', 'Wayanad'],
    'Madhya Pradesh': ['Bhopal', 'Indore', 'Gwalior', 'Jabalpur', 'Ujjain', 'Sagar', 'Ratlam', 'Satna', 'Rewa', 'Murwara', 'Singrauli', 'Burhanpur', 'Khandwa', 'Morena', 'Bhind', 'Chhindwara', 'Vidisha', 'Guna', 'Shivpuri', 'Damoh'],
    'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kalyan', 'Vasai-Virar', 'Navi Mumbai', 'Amravati', 'Nanded', 'Kolhapur', 'Sangli', 'Jalgaon', 'Akola', 'Latur', 'Dhule', 'Ahmednagar', 'Ichalkaranji', 'Jalna', 'Bhusawal', 'Panvel', 'Satara', 'Beed', 'Yavatmal', 'Kamptee', 'Gondia', 'Barshi', 'Achalpur'],
    'Manipur': ['Imphal', 'Thoubal', 'Kakching', 'Ukhrul', 'Bishnupur', 'Churachandpur', 'Senapati', 'Tamenglong', 'Jiribam', 'Moreh', 'Lilong', 'Moirang', 'Mayang Imphal'],
    'Meghalaya': ['Shillong', 'Tura', 'Nongpoh', 'Jowai', 'Nongstoin', 'Baghmara', 'Williamnagar', 'Resubelpara', 'Mankachar', 'Ampati'],
    'Mizoram': ['Aizawl', 'Lunglei', 'Saiha', 'Champhai', 'Kolasib', 'Serchhip', 'Lawngtlai', 'Mamit', 'Khawzawl', 'Hnahthial'],
    'Nagaland': ['Kohima', 'Dimapur', 'Mokokchung', 'Tuensang', 'Wokha', 'Zunheboto', 'Phek', 'Mon', 'Kiphire', 'Longleng', 'Peren'],
    'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Berhampur', 'Sambalpur', 'Puri', 'Baleshwar', 'Bhadrak', 'Baripada', 'Balangir', 'Jharsuguda', 'Bargarh', 'Rayagada', 'Jeypore', 'Dhenkanal', 'Barbil', 'Kendujhar', 'Paradip', 'Bhuban', 'Talcher'],
    'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda', 'Pathankot', 'Hoshiarpur', 'Batala', 'Moga', 'Abohar', 'Khanna', 'Phagwara', 'Muktsar', 'Barnala', 'Firozpur', 'Kapurthala', 'Sultanpur Lodhi', 'Fazilka', 'Gurdaspur', 'Malerkotla'],
    'Rajasthan': ['Jaipur', 'Jodhpur', 'Kota', 'Bikaner', 'Ajmer', 'Udaipur', 'Bhilwara', 'Alwar', 'Bharatpur', 'Sri Ganganagar', 'Sikar', 'Pali', 'Tonk', 'Chittorgarh', 'Hanumangarh', 'Beawar', 'Karauli', 'Dholpur', 'Bundi', 'Baran', 'Churu', 'Jhunjhunu', 'Nagaur', 'Jhalawar', 'Banswara'],
    'Sikkim': ['Gangtok', 'Namchi', 'Mangan', 'Gyalshing', 'Singtam', 'Rangpo', 'Jorethang', 'Pakyong', 'Rhenock', 'Soreng'],
    'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Tiruppur', 'Erode', 'Vellore', 'Thoothukudi', 'Dindigul', 'Thanjavur', 'Hosur', 'Nagercoil', 'Kanchipuram', 'Karaikudi', 'Neyveli', 'Cuddalore', 'Kumbakonam', 'Tiruvannamalai', 'Pollachi', 'Rajapalayam', 'Gudiyatham', 'Pudukkottai', 'Ambur'],
    'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Ramagundam', 'Khammam', 'Mahbubnagar', 'Nalgonda', 'Adilabad', 'Suryapet', 'Miryalaguda', 'Siddipet', 'Jagtial', 'Mancherial', 'Kamareddy', 'Kothagudem', 'Bhongir', 'Bodhan', 'Narayanpet', 'Wanaparthy'],
    'Tripura': ['Agartala', 'Udaipur', 'Dharmanagar', 'Kailashahar', 'Belonia', 'Khowai', 'Ambassa', 'Kumarghat', 'Sabroom', 'Teliamura'],
    'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Ghaziabad', 'Agra', 'Meerut', 'Varanasi', 'Allahabad', 'Bareilly', 'Aligarh', 'Moradabad', 'Saharanpur', 'Gorakhpur', 'Noida', 'Firozabad', 'Jhansi', 'Muzaffarnagar', 'Mathura', 'Rampur', 'Shahjahanpur', 'Farrukhabad', 'Fatehpur', 'Budaun', 'Sitapur', 'Hapur', 'Etawah', 'Unnao', 'Pilibhit', 'Lakhimpur', 'Basti', 'Deoria'],
    'Uttarakhand': ['Dehradun', 'Haridwar', 'Roorkee', 'Haldwani', 'Rudrapur', 'Kashipur', 'Rishikesh', 'Nainital', 'Mussoorie', 'Pithoragarh', 'Almora', 'Srinagar', 'Kotdwara', 'Ramnagar', 'Pauri', 'Chamoli Gopeshwar', 'New Tehri', 'Uttarkashi'],
    'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Bardhaman', 'Malda', 'Baharampur', 'Habra', 'Kharagpur', 'Shantipur', 'Ranaghat', 'Krishnanagar', 'Nabadwip', 'Medinipur', 'Chandannagar', 'Jalpaiguri', 'Baidyabati', 'Darjeeling', 'Purulia', 'Bankura', 'Alipurduar', 'Raiganj'],
    'Delhi': ['New Delhi', 'Delhi Cantonment', 'Narela', 'Najafgarh', 'Rohini', 'Dwarka', 'Patparganj'],
    'Jammu and Kashmir': ['Srinagar', 'Jammu', 'Anantnag', 'Baramulla', 'Sopore', 'Kathua', 'Udhampur', 'Poonch', 'Rajouri', 'Kupwara', 'Bandipora', 'Ganderbal', 'Pulwama', 'Shopian', 'Budgam', 'Kulgam', 'Doda', 'Ramban', 'Kishtwar', 'Reasi'],
    'Ladakh': ['Leh', 'Kargil', 'Drass', 'Nubra Valley', 'Zanskar'],
    'Puducherry': ['Puducherry', 'Karaikal', 'Mahe', 'Yanam'],
    'Andaman and Nicobar Islands': ['Port Blair', 'Diglipur', 'Mayabunder', 'Rangat', 'Car Nicobar'],
    'Chandigarh': ['Chandigarh'],
    'Dadra and Nagar Haveli and Daman and Diu': ['Silvassa', 'Daman', 'Diu'],
    'Lakshadweep': ['Kavaratti', 'Agatti', 'Amini', 'Andrott', 'Kadmat', 'Kalpeni', 'Kiltan', 'Minicoy']
  };

  const indianStates = Object.keys(indianStatesAndCities).sort();
  
  // Get cities for selected state
  const getCitiesForState = (state) => {
    return state ? (indianStatesAndCities[state] || []).sort() : [];
  };

  // If OTP verification is shown, render OTP screen
  if (showOTPVerification) {
    return (
      <div className="signup-container">
        <div className="signup-card">
          <div className="signup-header">
            <button onClick={() => setShowOTPVerification(false)} className="back-link">
              ← Back
            </button>
            <h1 className="signup-title">Verify Your Email</h1>
            <p className="signup-subtitle">
              We've sent a 6-digit OTP to <strong>{verificationEmail}</strong>
            </p>
            <p className="otp-hint">Please enter the OTP below to verify your email</p>
          </div>

          <div className="otp-verification">
            <div className="otp-input-container">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  className={`otp-input ${otpError ? 'error' : ''}`}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handleOtpPaste}
                  maxLength={1}
                  autoFocus={index === 0}
                />
              ))}
            </div>
            {otpError && (
              <span className="field-error otp-error">{otpError}</span>
            )}
            <p className="otp-demo-hint">
              <strong>Demo OTP:</strong> {generatedOTP}
            </p>
            <div className="otp-actions">
              <button type="button" className="resend-button" onClick={handleResendOTP}>
                Resend OTP
              </button>
              <button type="button" className="verify-button" onClick={handleVerifyOTP} disabled={isSubmitting}>
                {isSubmitting ? 'Creating Account...' : 'Verify OTP'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-container">
      <div className="signup-card">
        <div className="signup-header">
          <Link to="/login" className="back-link">← Back to Login</Link>
          <h1 className="signup-title">Create Customer Account</h1>
          <p className="signup-subtitle">Sign up to start booking trucks</p>
        </div>

        <form className="signup-form" onSubmit={handleSubmit}>
          {/* Customer Name */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customerName" className="form-label">
                Customer Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="customerName"
                name="customerName"
                className={`form-input ${errors.customerName ? 'error' : ''}`}
                value={formData.customerName}
                onChange={handleChange}
                placeholder="Enter customer name"
              />
              {errors.customerName && (
                <span className="field-error">{errors.customerName}</span>
              )}
            </div>
          </div>

          {/* PAN Number and Aadhar Number */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label htmlFor="panNumber" className="form-label">
                PAN Number
              </label>
              <input
                type="text"
                id="panNumber"
                name="panNumber"
                className="form-input"
                value={formData.panNumber}
                onChange={handleChange}
                placeholder="Enter PAN number"
              />
            </div>
            <div className="form-group">
              <label htmlFor="aadharNumber" className="form-label">
                Aadhar Number
              </label>
              <input
                type="text"
                id="aadharNumber"
                name="aadharNumber"
                className="form-input"
                value={formData.aadharNumber}
                onChange={handleChange}
                placeholder="Enter Aadhar number"
              />
            </div>
          </div>

          {/* Address Line 1 */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="addressLine1" className="form-label">
                Address Line 1
              </label>
              <input
                type="text"
                id="addressLine1"
                name="addressLine1"
                className="form-input"
                value={formData.addressLine1}
                onChange={handleChange}
                placeholder="Enter address line 1"
              />
            </div>
          </div>

          {/* Address Line 2 */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="addressLine2" className="form-label">
                Address Line 2
              </label>
              <input
                type="text"
                id="addressLine2"
                name="addressLine2"
                className="form-input"
                value={formData.addressLine2}
                onChange={handleChange}
                placeholder="Enter address line 2"
              />
            </div>
          </div>

          {/* State and City */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label htmlFor="state" className="form-label">
                State
              </label>
              <select
                id="state"
                name="state"
                className="form-input"
                value={formData.state}
                onChange={(e) => {
                  setFormData({ ...formData, state: e.target.value, city: '' });
                  if (errors.state) setErrors({...errors, state: ''});
                }}
              >
                <option value="">Select State</option>
                {indianStates.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="city" className="form-label">
                City
              </label>
              <select
                id="city"
                name="city"
                className="form-input"
                value={formData.city}
                onChange={handleChange}
                disabled={!formData.state}
              >
                <option value="">{formData.state ? 'Select City' : 'Select State first'}</option>
                {getCitiesForState(formData.state).map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Pincode and Country */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label htmlFor="pincode" className="form-label">
                Pincode
              </label>
              <input
                type="text"
                id="pincode"
                name="pincode"
                className="form-input"
                value={formData.pincode}
                onChange={handleChange}
                placeholder="Enter pincode"
              />
            </div>
            <div className="form-group">
              <label htmlFor="country" className="form-label">
                Country
              </label>
              <select
                id="country"
                name="country"
                className="form-input"
                value={formData.country}
                onChange={handleChange}
              >
                <option value="India">India</option>
              </select>
            </div>
          </div>

          {/* Email ID */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="emailId" className="form-label">
                Email Address
              </label>
              <input
                type="email"
                id="emailId"
                name="emailId"
                className={`form-input ${errors.emailId ? 'error' : ''}`}
                value={formData.emailId}
                onChange={handleChange}
                placeholder="Enter your email"
              />
              {errors.emailId && (
                <span className="field-error">{errors.emailId}</span>
              )}
            </div>
          </div>

          {/* Mobile Number and Alternate Contact */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label htmlFor="mobileNumber" className="form-label">
                Mobile Number <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="mobileNumber"
                name="mobileNumber"
                className={`form-input ${errors.mobileNumber ? 'error' : ''}`}
                value={formData.mobileNumber}
                onChange={handleChange}
                placeholder="Enter 10-digit mobile number"
                maxLength="10"
              />
              {errors.mobileNumber && (
                <span className="field-error">{errors.mobileNumber}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="alternateContactNumber" className="form-label">
                Alternate Contact Number
              </label>
              <input
                type="tel"
                id="alternateContactNumber"
                name="alternateContactNumber"
                className="form-input"
                value={formData.alternateContactNumber}
                onChange={handleChange}
                placeholder="Enter alternate contact"
                maxLength="10"
              />
            </div>
          </div>

          {/* Photo Upload */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Photo</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '100px', height: '100px', border: '2px dashed #e0e0e0', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                  {formData.photoPreview ? (
                    <>
                      <img src={formData.photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({...formData, photo: null, photoPreview: null});
                          document.getElementById('customer-photo-input').value = '';
                        }}
                        style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.7)', color: 'white', border: 'none', borderRadius: '4px', padding: '2px 6px', fontSize: '0.7rem', cursor: 'pointer' }}
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '1.5rem' }}>📷</div>
                      <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '4px' }}>Upload</div>
                    </>
                  )}
                  <input
                    type="file"
                    id="customer-photo-input"
                    accept="image/jpeg,image/png"
                    onChange={handlePhotoUpload}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                  />
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  JPG or PNG (max 5MB)
                </div>
              </div>
            </div>
          </div>

          {/* Password */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password <span className="required">*</span>
              </label>
              <input
                type="password"
                id="password"
                name="password"
                className={`form-input ${errors.password ? 'error' : ''}`}
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter password (min 6 characters)"
              />
              {errors.password && (
                <span className="field-error">{errors.password}</span>
              )}
            </div>
          </div>

          {/* Confirm Password */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password <span className="required">*</span>
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
              />
              {errors.confirmPassword && (
                <span className="field-error">{errors.confirmPassword}</span>
              )}
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="terms"
                checked={formData.terms}
                onChange={handleChange}
                className="checkbox-input"
              />
              <span className="checkbox-text">
                I agree to the <a href="#terms" className="link">Terms and Conditions</a> and <a href="#privacy" className="link">Privacy Policy</a>
              </span>
            </label>
            {errors.terms && (
              <span className="field-error">{errors.terms}</span>
            )}
          </div>

          {errors.general && (
            <div style={{ backgroundColor: '#fee2e2', color: '#b91c1c', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '0.9rem' }}>
              {errors.general}
            </div>
          )}

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="signup-footer">
          <p>
            Already have an account? <Link to="/login" className="link">Login here</Link>
          </p>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="popup-overlay" onClick={handleClosePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-icon success">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M16 24L21 29L32 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="popup-title">Account Created Successfully!</h2>
            <p className="popup-message">
              Your account has been created successfully!
            </p>
            <p className="popup-submessage">
              Your account is now active. You can login with your credentials.
            </p>
            <button className="popup-button" onClick={handleClosePopup}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSignup;

